import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TaskService } from 'service/task.service';
import { Operation } from 'model/alerts.model';
import { TaskFilters } from '../../TasksFilters/TasksFilters';
import { TaskData } from '../types';
import { TaskType } from '../constants';
import { buildQueryParams, normalizeStatus } from '../utils';
import { Task } from 'model/tasks.model';

type UseTasksTableProps = {
  mine: boolean;
  filters?: TaskFilters;
  onError: (message: string) => void;
  onStartOperation?: (operation: Operation, message: string) => void;
};

export const useTasksTable = ({ mine, filters, onError, onStartOperation }: UseTasksTableProps) => {
  const params = useParams();
  const onErrorRef = useRef(onError);
  const onStartOperationRef = useRef<((operation: Operation, message: string) => void) | undefined>(onStartOperation);
  const filtersRef = useRef(filters);
  const paramsRef = useRef(params);

  useEffect(() => {
    onErrorRef.current = onError;
    onStartOperationRef.current = onStartOperation;
    filtersRef.current = filters;
    paramsRef.current = params;
  }, [onError, onStartOperation, filters, params]);

  const [toDoData, setToDoData] = useState<TaskData>({
    totalRecords: 0,
    records: [],
    totalFilteredRecords: 0,
  });

  const [inProgressData, setInProgressData] = useState<TaskData>({
    totalRecords: 0,
    records: [],
    totalFilteredRecords: 0,
  });

  const [doneData, setDoneData] = useState<TaskData>({
    totalRecords: 0,
    records: [],
    totalFilteredRecords: 0,
  });

  const [toDoLoading, setToDoLoading] = useState(true);
  const [inProgressLoading, setInProgressLoading] = useState(true);
  const [doneLoading, setDoneLoading] = useState(true);

  const fetchAllTasks = useCallback(async () => {
    setToDoLoading(true);
    setInProgressLoading(true);
    setDoneLoading(true);

    try {
      const queryParams = buildQueryParams(mine, paramsRef.current, filtersRef.current);
      const response = await TaskService.getListView(queryParams);

      const allRecords = response.data || [];
      const uniqueRecords = allRecords.filter(
        (task: any, index: number, self: any[]) =>
          index === self.findIndex((t: any) => t.id === task.id),
      );

      const tasksByStatus = uniqueRecords.reduce(
        (acc, task: any) => {
          const status = normalizeStatus(task.status || '');
          acc[status].push(task);
          return acc;
        },
        { TODO: [], IN_PROGRESS: [], DONE: [] } as Record<TaskType, Task[]>
      );

      setToDoData({
        totalRecords: tasksByStatus.TODO.length,
        records: tasksByStatus.TODO,
        totalFilteredRecords: tasksByStatus.TODO.length,
      });

      setInProgressData({
        totalRecords: tasksByStatus.IN_PROGRESS.length,
        records: tasksByStatus.IN_PROGRESS,
        totalFilteredRecords: tasksByStatus.IN_PROGRESS.length,
      });

      setDoneData({
        totalRecords: tasksByStatus.DONE.length,
        records: tasksByStatus.DONE,
        totalFilteredRecords: tasksByStatus.DONE.length,
      });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Something went wrong';
      onErrorRef.current(errorMessage);
    } finally {
      setToDoLoading(false);
      setInProgressLoading(false);
      setDoneLoading(false);
    }
  }, [mine]);

  const prevFiltersStringRef = useRef<string>('');
  const prevParamsIdRef = useRef<string | undefined>(undefined);
  const prevMineRef = useRef<boolean>(mine);

  useEffect(() => {
    const filtersString = JSON.stringify(filters);
    const paramsId = params?.id;
    const filtersChanged = filtersString !== prevFiltersStringRef.current;
    const paramsChanged = paramsId !== prevParamsIdRef.current;
    const mineChanged = mine !== prevMineRef.current;

    if (filtersChanged || paramsChanged || mineChanged) {
      prevFiltersStringRef.current = filtersString;
      prevParamsIdRef.current = paramsId;
      prevMineRef.current = mine;
      fetchAllTasks();
    }
  }, [mine, filters, params?.id, fetchAllTasks]);

  const allTasks = useMemo(
    () => [...toDoData.records, ...inProgressData.records, ...doneData.records],
    [toDoData.records, inProgressData.records, doneData.records]
  );

  return {
    toDoData,
    inProgressData,
    doneData,
    toDoLoading,
    inProgressLoading,
    doneLoading,
    fetchAllTasks,
    allTasks,
  };
};
